import type {
  ContentAlignment,
  FormJourneyItem,
  JourneyItemMediaPosition,
} from '@plura/shared/types';
import type { LayoutStyle } from './types';

/**
 * Hard invariants borrowed from createFormWithAi.ts `sanitizeDesign`:
 * content alignment must be opposite of media side, mobile layout must
 * mirror desktop layout family, and opacity is clamped per layout.
 */
function sanitizeDesign(input: {
  mediaPosition: JourneyItemMediaPosition;
  contentAlignment: ContentAlignment;
  opacity?: number | null;
}): {
  mediaPosition: JourneyItemMediaPosition;
  mobileMediaPosition: JourneyItemMediaPosition;
  contentAlignment: ContentAlignment;
  opacity: number;
} {
  const pos = input.mediaPosition;
  let align = input.contentAlignment;
  let opacity = input.opacity ?? 50;
  let mobile: JourneyItemMediaPosition;

  if (pos === 'SPLIT_LEFT' || pos === 'DETATCHED_LEFT') {
    align = 'RIGHT';
  } else if (pos === 'SPLIT_RIGHT' || pos === 'DETATCHED_RIGHT') {
    align = 'LEFT';
  }

  if (pos === 'FULL') {
    mobile = 'MOBILE_FULL';
  } else if (pos.startsWith('SPLIT') || pos.startsWith('DETATCHED')) {
    mobile = 'MOBILE_POSTER_TOP';
  } else if (pos.startsWith('INLINE')) {
    mobile = 'MOBILE_INLINE_CENTER';
  } else {
    mobile = 'MOBILE_FULL';
  }

  if (pos.startsWith('SPLIT') || pos.startsWith('DETATCHED') || pos.startsWith('INLINE')) {
    opacity = 100;
  } else if (pos === 'FULL' && opacity > 70) {
    opacity = 55;
  }

  return { mediaPosition: pos, mobileMediaPosition: mobile, contentAlignment: align, opacity };
}

interface LayoutDecision {
  mediaPosition: JourneyItemMediaPosition;
  contentAlignment: ContentAlignment;
  opacity: number;
}

/**
 * Pick a layout for a given question based on its type, position in the form,
 * and the chosen layout style. Mirrors the design patterns enumerated in the
 * createFormWithAi.ts system prompt.
 */
function pickLayout(
  item: FormJourneyItem,
  index: number,
  style: LayoutStyle,
): LayoutDecision {
  // Never pick a side layout for an item without media: SPLIT_*/DETATCHED_*
  // reserve half the screen for the (missing) media and LEFT/RIGHT alignment
  // crams the content against one edge. This matters when the media pool is
  // empty or the layouts tier is applied without the visuals tier.
  if (!item.media) {
    return { mediaPosition: 'FULL', contentAlignment: 'CENTER', opacity: 50 };
  }

  const isMc6Plus = item.type === 'MULTIPLE_CHOICE' && (item.selectOptions?.length ?? 0) >= 6;
  const isCenteredType = item.type === 'RATING'
    || item.type === 'NPS'
    || item.type === 'OPTION_SCALE'
    || item.type === 'STATEMENT'
    || item.type === 'END_SCREEN'
    || item.type === 'REDIRECT_SCREEN';

  if (isMc6Plus) {
    return { mediaPosition: 'FULL', contentAlignment: 'CENTER', opacity: 45 };
  }

  if (style === 'cinematic') {
    if (item.type === 'WELCOME_SCREEN') {
      return { mediaPosition: 'FULL', contentAlignment: 'CENTER', opacity: 50 };
    }
    return { mediaPosition: 'FULL', contentAlignment: 'CENTER', opacity: 50 };
  }

  if (style === 'editorial') {
    if (item.type === 'WELCOME_SCREEN') {
      return { mediaPosition: 'DETATCHED_RIGHT', contentAlignment: 'LEFT', opacity: 100 };
    }
    if (isCenteredType) {
      return { mediaPosition: 'FULL', contentAlignment: 'CENTER', opacity: 50 };
    }
    return index % 2 === 0
      ? { mediaPosition: 'DETATCHED_RIGHT', contentAlignment: 'LEFT', opacity: 100 }
      : { mediaPosition: 'DETATCHED_LEFT', contentAlignment: 'RIGHT', opacity: 100 };
  }

  // visualSplit (default)
  if (item.type === 'WELCOME_SCREEN') {
    return { mediaPosition: 'SPLIT_RIGHT', contentAlignment: 'LEFT', opacity: 100 };
  }
  if (isCenteredType) {
    return { mediaPosition: 'FULL', contentAlignment: 'CENTER', opacity: 50 };
  }
  return index % 2 === 0
    ? { mediaPosition: 'SPLIT_RIGHT', contentAlignment: 'LEFT', opacity: 100 }
    : { mediaPosition: 'SPLIT_LEFT', contentAlignment: 'RIGHT', opacity: 100 };
}

/**
 * Apply layout decisions to every journey item and enforce invariants.
 * Returns a new array of items; does not mutate the input.
 */
export function applyLayouts(
  journeyItems: FormJourneyItem[],
  style: LayoutStyle,
): FormJourneyItem[] {
  return journeyItems.map((item, index) => {
    const decision = pickLayout(item, index, style);
    const sanitized = sanitizeDesign(decision);
    const isMc6Plus = item.type === 'MULTIPLE_CHOICE' && (item.selectOptions?.length ?? 0) >= 6;

    return {
      ...item,
      mediaPosition: sanitized.mediaPosition,
      mobileMediaPosition: sanitized.mobileMediaPosition,
      contentAlignment: sanitized.contentAlignment,
      opacity: sanitized.opacity,
      horizontalAlignment: isMc6Plus ? true : item.horizontalAlignment,
    };
  });
}
